import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  CreditCard,
  Send,
  MessageSquare,
  Clock,
  BookOpen,
  DollarSign,
  User,
  Mail,
  Phone
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import bookingsService, { Booking } from '../services/bookingsService'
import { messageService, Message } from '../services/messageService'
import { formatBookingSelectedPeriod } from '../utils/courseSchedule'

const BookingDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [booking, setBooking] = useState<Booking | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [processing, setProcessing] = useState(false)

  // Scroll to top on initial mount
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 100)
    
    requestAnimationFrame(() => {
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    })

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (id) {
      // Scroll to top when booking ID changes
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      fetchBooking()
    }
  }, [id])

  useEffect(() => {
    if (booking?._id) {
      fetchMessages()
    }
  }, [booking?._id])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const fetchBooking = async () => {
    if (!id) return
    setLoading(true)
    setError('')
    try {
      const response = await bookingsService.getBooking(id)
      if (response.success && response.data) {
        setBooking(response.data)
      } else {
        setError('Booking not found')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch booking')
    } finally {
      setLoading(false)
    }
  }

  const fetchMessages = async () => {
    if (!booking?._id || !booking?.schoolId) return
    setLoadingMessages(true)
    try {
      const response = await messageService.getMessages(undefined, booking.schoolId, booking._id)
      const fetchedMessages = response.data.messages || []
      setMessages(fetchedMessages)

      const unreadIds = fetchedMessages
        .filter((msg: Message) => !msg.isRead && 
          (typeof msg.receiverId === 'object' ? msg.receiverId._id : msg.receiverId) === (user?.id || user?._id))
        .map((msg: Message) => msg._id)

      if (unreadIds.length > 0) {
        await messageService.markAsRead(unreadIds)
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            unreadIds.includes(msg._id) ? { ...msg, isRead: true } : msg
          )
        )
      }
    } catch (err: any) {
      console.error('Error fetching messages:', err)
    } finally {
      setLoadingMessages(false)
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() || sending || !booking?.schoolId || !booking?._id) return
    
    if (!isMessagingAllowed()) {
      alert(getMessagingDisabledMessage())
      return
    }

    const messageContent = newMessage.trim()
    setSending(true)
    try {
      // Extract schoolId string if it's an object
      const schoolIdString = typeof booking.schoolId === 'object' && booking.schoolId !== null
        ? (booking.schoolId as any)?._id || (booking.schoolId as any).id
        : booking.schoolId
      
      // Extract userId string if it's an object (receiver - the customer who made the booking)
      const userId = typeof booking.userId === 'object' && booking.userId !== null 
        ? (booking.userId as any)?._id || booking.userId 
        : booking.userId;
      
      await messageService.sendMessage(String(schoolIdString), messageContent, userId ? String(userId) : undefined, booking._id)
      setNewMessage('')
      await fetchMessages()
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || 'Failed to send message.')
    } finally {
      setSending(false)
    }
  }

  const scrollToBottom = () => {
    // Scroll only the messages container, not the entire page
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  const isMessagingAllowed = () => {
    if (!booking) return false
    const allowedStatuses = ['pending', 'approved']
    return allowedStatuses.includes(booking.status)
  }

  const getMessagingDisabledMessage = () => {
    if (!booking) return ''
    if (booking.status === 'rejected') {
      return 'Messaging is disabled for rejected bookings.'
    }
    if (booking.status === 'cancelled') {
      return 'Messaging is disabled for cancelled bookings.'
    }
    return 'Messaging is not available for this booking.'
  }

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }


  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string; icon: any }> = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Pending', icon: Clock },
      approved: { color: 'bg-primary-100 text-primary-800 border-primary-200', text: 'Approved', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', text: 'Rejected', icon: XCircle },
      completed: { color: 'bg-primary-100 text-primary-800 border-primary-200', text: 'Completed', icon: CheckCircle },
      cancelled: { color: 'bg-gray-100 text-gray-800 border-gray-200', text: 'Cancelled', icon: XCircle },
    }
    const config = statusConfig[status] ?? {
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      text: status || 'Unknown',
      icon: AlertCircle,
    }
    const Icon = config.icon
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border flex items-center space-x-1 ${config.color}`}>
        <Icon className="h-2.5 w-2.5" />
        <span>{config.text}</span>
      </span>
    )
  }

  /**
   * Snapshot stored on each timeline row: top-level `status` stays "pending" until payment
   * (SCHOOL_APPROVED is still "pending" because the booking is only fully confirmed after pay).
   * This badge explains the *step*, not a duplicate error.
   */
  const getTimelineStateBadge = (entry: {
    action: string
    substatus?: 'pending payment' | 'pending approval from school'
    status: 'pending' | 'approved' | 'cancelled' | 'rejected'
  }) => {
    const action = entry.action
    if (action === 'BOOKING_CREATED' || action === 'ENQUIRY') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold border flex items-center space-x-1 bg-amber-50 text-amber-900 border-amber-200">
          <Clock className="h-2.5 w-2.5" />
          <span>Pending — awaiting school</span>
        </span>
      )
    }
    if (action === 'SCHOOL_APPROVED') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold border flex items-center space-x-1 bg-orange-50 text-orange-900 border-orange-200">
          <Clock className="h-2.5 w-2.5" />
          <span>Pending — awaiting payment</span>
        </span>
      )
    }
    if (action === 'PAYMENT_COMPLETED') {
      return getStatusBadge('approved')
    }
    if (action === 'SCHOOL_REJECTED' || entry.status === 'rejected') {
      return getStatusBadge('rejected')
    }
    if (entry.status === 'cancelled') {
      return getStatusBadge('cancelled')
    }
    if (entry.substatus === 'pending payment') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold border flex items-center space-x-1 bg-orange-50 text-orange-900 border-orange-200">
          <Clock className="h-2.5 w-2.5" />
          <span>Pending — awaiting payment</span>
        </span>
      )
    }
    if (entry.substatus === 'pending approval from school') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold border flex items-center space-x-1 bg-amber-50 text-amber-900 border-amber-200">
          <Clock className="h-2.5 w-2.5" />
          <span>Pending — awaiting school</span>
        </span>
      )
    }
    return getStatusBadge(entry.status)
  }

  const getActionStatusBadge = (actionStatus?: string) => {
    if (!actionStatus) return null
    const statusConfig: Record<string, { color: string; text: string }> = {
      Y_PENDING: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending School' },
      U_PENDING: { color: 'bg-orange-100 text-orange-800', text: 'Pending Payment' },
    }
    const config = statusConfig[actionStatus]
    if (!config) return null
    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${config.color}`}>
        {config.text}
      </span>
    )
  }

  const shouldShowActionButtons = (booking: Booking | null): boolean => {
    if (!booking || !booking.status) {
      return false
    }
    
    const isPending = booking.status === 'pending'
    
    // If substatus exists, check it matches 'pending approval from school'
    // If substatus doesn't exist (legacy bookings), show buttons for pending status
    if (booking.substatus) {
      // Normalize substatus for comparison (trim whitespace)
      const normalizedSubstatus = booking.substatus.trim()
      const isPendingApproval = normalizedSubstatus === 'pending approval from school'
      
      // Debug log for troubleshooting
      console.log('BookingDetails - Button visibility check:', {
        bookingId: booking._id,
        status: booking.status,
        substatus: booking.substatus,
        normalizedSubstatus,
        isPending,
        isPendingApproval,
        willShow: isPending && isPendingApproval
      })
      
      return isPending && isPendingApproval
    }
    
    // For legacy bookings without substatus, show buttons if status is pending
    console.log('BookingDetails - Legacy booking (no substatus):', {
      bookingId: booking._id,
      status: booking.status,
      willShow: isPending
    })
    return isPending
  }

  const handleApprove = async () => {
    if (!booking || processing) return
    setProcessing(true)
    try {
      const response = await bookingsService.approveBooking(booking._id)
      if (response.success) {
        setShowApproveDialog(false)
        await fetchBooking()
      } else {
        alert('Failed to approve booking. Please try again.')
      }
    } catch (error) {
      console.error('Error approving booking:', error)
      alert('Failed to approve booking. Please try again.')
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async () => {
    if (!booking || processing) return
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection.')
      return
    }
    setProcessing(true)
    try {
      const response = await bookingsService.rejectBooking(booking._id, rejectReason.trim())
      if (response.success) {
        setShowRejectDialog(false)
        setRejectReason('')
        await fetchBooking()
      } else {
        alert('Failed to reject booking. Please try again.')
      }
    } catch (error) {
      console.error('Error rejecting booking:', error)
      alert('Failed to reject booking. Please try again.')
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border border-gray-200 max-w-md w-full text-center"
        >
          <div className="p-4 bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-10 w-10 text-red-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Booking Not Found</h3>
          <p className="text-gray-600 mb-6">{error || 'The booking you are looking for does not exist.'}</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="space-y-4 p-4">
        {/* Header */}
        <div className="relative overflow-hidden bg-[#f5f5f5] rounded-2xl shadow-xl border border-gray-200">
          <div className="relative p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">Booking Details</h1>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 text-gray-700">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">{booking.name}</span>
                  </div>
                  <span className="text-gray-400">•</span>
                  <div className="flex items-center gap-1.5 text-gray-700">
                    <BookOpen className="w-4 h-4" />
                    <span className="text-sm">{booking.courseTitle || 'Course'}</span>
                  </div>
                </div>
              </div>
              {shouldShowActionButtons(booking) && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowRejectDialog(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-medium text-sm shadow-lg hover:shadow-xl"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setShowApproveDialog(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-medium text-sm shadow-lg hover:shadow-xl"
                  >
                    Approve
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Split Layout */}
        <div className="flex gap-4">
          {/* Left Side - Booking Details (60%) */}
          <div className="w-[60%] flex-shrink-0">
            <div className="space-y-3">
              {/* Customer Information */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                  <div className="p-1.5 bg-primary-100 rounded-lg">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">Customer Information</h2>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-0.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</label>
                    <p className="text-sm font-semibold text-gray-900">{booking.name}</p>
                  </div>
                  <div className="space-y-0.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</label>
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-sm text-gray-900">{booking.email}</p>
                    </div>
                  </div>
                  {booking.phone && (
                    <div className="space-y-0.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</label>
                      <div className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        <p className="text-sm text-gray-900">{booking.phone}</p>
                      </div>
                    </div>
                  )}
                  <div className="space-y-0.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                    <div className="mt-0.5 flex items-center flex-wrap gap-1.5">
                      {getStatusBadge(booking.status)}
                      {getActionStatusBadge(booking.substatus)}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Booking Information */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                  <div className="p-1.5 bg-emerald-100 rounded-lg">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">Booking Information</h2>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-0.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Course</label>
                    <div className="flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-sm font-semibold text-gray-900">{booking.courseTitle || 'N/A'}</p>
                    </div>
                  </div>
                  {booking.selectedMonth && (
                    <div className="space-y-0.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Course Dates</label>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <p className="text-sm text-gray-900">
                          {formatBookingSelectedPeriod(booking.selectedMonth, 'long')}
                        </p>
                      </div>
                    </div>
                  )}
                  {booking.accommodationType && (
                    <div className="space-y-0.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Accommodation Type</label>
                      <p className="text-sm text-gray-900">{booking.accommodationType}</p>
                    </div>
                  )}
                  <div className="space-y-0.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Submitted</label>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-sm text-gray-900">{formatDateTime(booking.submittedAt)}</p>
                    </div>
                  </div>
                  {booking.approvedAt && (
                    <div className="space-y-0.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Approved</label>
                      <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5 text-primary-600" />
                        <p className="text-sm text-gray-900">{formatDateTime(booking.approvedAt)}</p>
                      </div>
                    </div>
                  )}
                  {booking.rejectedAt && (
                    <div className="space-y-0.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rejected</label>
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-red-500" />
                        <p className="text-sm text-gray-900">{formatDateTime(booking.rejectedAt)}</p>
                      </div>
                    </div>
                  )}
                  {booking.paymentCompletedAt && (
                    <div className="space-y-0.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment Completed</label>
                      <div className="flex items-center gap-1.5">
                        <CreditCard className="w-3.5 h-3.5 text-primary-600" />
                        <p className="text-sm text-gray-900">{formatDateTime(booking.paymentCompletedAt)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Message */}
              {booking.message && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                    <div className="p-1.5 bg-purple-100 rounded-lg">
                      <MessageSquare className="w-4 h-4 text-purple-600" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900">Message</h2>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 p-3 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{booking.message}</p>
                  </div>
                </motion.div>
              )}

              {/* Payment Link */}
              {booking.paymentLink && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                    <div className="p-1.5 bg-blue-100 rounded-lg">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900">Payment Link</h2>
                  </div>
                  <a 
                    href={booking.paymentLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-medium text-xs break-all bg-primary-50 px-3 py-1.5 rounded-lg hover:bg-primary-100 transition-colors"
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    {booking.paymentLink}
                  </a>
                </motion.div>
              )}

              {/* Booking Timeline */}
              {booking.actionTimeline && booking.actionTimeline.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                  className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                    <div className="p-1.5 bg-amber-100 rounded-lg">
                      <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900">Booking Timeline</h2>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Newest first. The booking stays <strong>pending</strong> until payment is received; after school approval
                    the overall status is still pending while the user pays, then it becomes <strong>approved</strong> when
                    payment completes.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-dashed border-gray-300">
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-600 uppercase tracking-wider">Action</th>
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-600 uppercase tracking-wider">State after event</th>
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-600 uppercase tracking-wider">Performed By</th>
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-600 uppercase tracking-wider">Date</th>
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-600 uppercase tracking-wider">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {([...booking.actionTimeline]
                          .sort(
                            (a, b) =>
                              new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime()
                          )
                          .map((timeline) => (
                          <tr
                            key={`${timeline.action}-${String(timeline.performedAt)}-${timeline.performedBy}`}
                            className="border-b border-dashed border-gray-200 hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-2 px-3 text-xs font-medium text-gray-900">{timeline.action}</td>
                            <td className="py-2 px-3 text-xs">
                              {getTimelineStateBadge(timeline)}
                            </td>
                            <td className="py-2 px-3 text-xs text-gray-700">{timeline.performedBy}</td>
                            <td className="py-2 px-3 text-xs text-gray-700">{formatDateTime(timeline.performedAt)}</td>
                            <td className="py-2 px-3 text-xs text-gray-600">{timeline.notes || '-'}</td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Right Side - Messages (40%) */}
          <div className="w-[40%] flex-shrink-0">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 flex flex-col h-[calc(100vh-200px)] overflow-hidden"
            >
              {/* Messages Header */}
              <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-primary-100/50">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary-600 rounded-lg">
                    <MessageSquare className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">Messages</h2>
                </div>
              </div>

              {/* Messages List */}
              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-3 bg-gradient-to-b from-gray-50/50 to-white"
              >
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                      <p className="text-xs text-gray-500">Loading messages...</p>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="p-3 bg-gray-100 rounded-full mb-3">
                      <MessageSquare className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-xs font-medium text-gray-600">No messages yet</p>
                    <p className="text-xs text-gray-500 mt-0.5">Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {messages.map((message) => {
                      // Extract senderId from message (handle both object and string formats)
                      const messageSenderId = typeof message.senderId === 'object' && message.senderId !== null
                        ? (message.senderId as any)._id || (message.senderId as any).id
                        : message.senderId
                      
                      // Extract current user ID (ensure it's a string)
                      // User model has 'id' not '_id' in yoga admin
                      const currentUserId = user?.id ? String(user.id) : (user?._id ? String(user._id) : null)
                      
                      // Compare IDs (convert both to strings for reliable comparison)
                      const isSender = currentUserId && messageSenderId && String(messageSenderId) === String(currentUserId)
                      
                      const sender = typeof message.senderId === 'object' && message.senderId !== null
                        ? message.senderId
                        : { firstName: 'User', lastName: '', email: '' }

                      return (
                        <motion.div
                          key={message._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] rounded-lg px-3 py-2 shadow-sm ${
                            isSender
                              ? 'bg-gradient-to-br from-primary-600 to-primary-700 text-white'
                              : 'bg-white text-gray-900 border-2 border-gray-200'
                          }`}>
                            {!isSender && (
                              <p className="text-xs font-bold mb-1 opacity-90">
                                {sender.firstName} {sender.lastName}
                              </p>
                            )}
                            <p className="text-xs whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
                            <p className={`text-xs mt-1 ${isSender ? 'text-white/70' : 'text-gray-500'}`}>
                              {formatMessageTime(message.createdAt)}
                            </p>
                          </div>
                        </motion.div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="p-3 border-t border-gray-200 bg-white">
                {!isMessagingAllowed() ? (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-lg p-2.5">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 font-medium">{getMessagingDisabledMessage()}</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 transition-all"
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 font-medium text-sm shadow-lg hover:shadow-xl"
                    >
                      {sending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Approve Confirmation Dialog */}
      {showApproveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={() => !processing && setShowApproveDialog(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Approve Booking</h3>
                <p className="text-sm text-gray-600">Are you sure you want to approve this booking?</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowApproveDialog(false)}
                disabled={processing}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all font-medium text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={processing}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all font-medium text-sm shadow-lg disabled:opacity-50 flex items-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Approving...
                  </>
                ) : (
                  'Approve'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Reject Confirmation Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={() => !processing && setShowRejectDialog(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Reject Booking</h3>
                <p className="text-sm text-gray-600">Please provide a reason for rejection</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter the reason for rejecting this booking..."
                rows={4}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 transition-all resize-none"
                disabled={processing}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectDialog(false)
                  setRejectReason('')
                }}
                disabled={processing}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all font-medium text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-medium text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  'Reject Booking'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default BookingDetails

